import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { getApi, postApi, deleteApi, patchApi } from '../../Services/apiService';
import { AdminStatCard, AdminBadge, AdminModal, AdminModalFooter } from '../components/AdminUi';
import Input from '../../Components/Input/Input';
import Button from '../../Components/Button/Button';

const MAX_ANSWERS = 4;
const TYPE_TEXT = 1;
const TYPE_MCQ = 2;
const TYPE_YES_NO = 3;
const YES_NO_ANSWERS = ['Yes', 'No'];

const defaultStats = {
  total_questions: 0,
  active_questions: 0,
  required_active: 0,
};

function isTruthyFlag(value) {
  return value === '1' || value === 1 || value === true;
}

function normalizeQuestion(question) {
  const type = Number(question.type) || TYPE_MCQ;
  const answers = (question.options || question.answers || [])
    .map((option) =>
      typeof option === 'string' ? option : option.option_name || option.name || option.text || ''
    )
    .filter(Boolean);

  return {
    id: question.id,
    question: question.question,
    type,
    required: isTruthyFlag(question.is_required ?? question.required),
    active: isTruthyFlag(question.is_active ?? question.active),
    deleted: isTruthyFlag(question.is_deleted ?? question.deleted),
    answers: type === TYPE_YES_NO && answers.length === 0 ? YES_NO_ANSWERS : answers,
  };
}

function questionTypeLabel(type) {
  if (type === TYPE_TEXT) return 'Text';
  if (type === TYPE_YES_NO) return 'Yes / No';
  return 'MCQ';
}

function formTypeFromQuestionType(type) {
  if (type === TYPE_TEXT) return 'text';
  if (type === TYPE_YES_NO) return 'yesno';
  return 'mcq';
}

function questionTypeFromFormType(formType) {
  if (formType === 'text') return TYPE_TEXT;
  if (formType === 'yesno') return TYPE_YES_NO;
  return TYPE_MCQ;
}

function buildQuestionPayload(questionText, formType, answers) {
  const type = questionTypeFromFormType(formType);
  const payload = {
    question: questionText.trim(),
    type,
  };

  if (type === TYPE_MCQ) {
    payload.options = answers;
  }

  return payload;
}

function buildEditQuestionPayload(questionText, questionType, answers, { restore = false } = {}) {
  const payload = {
    question: questionText.trim(),
  };

  if (questionType === TYPE_MCQ) {
    payload.options = answers;
  }

  if (restore) {
    payload.restore = true;
    payload.is_deleted = 0;
  }

  return payload;
}

function computeStats(questionsList) {
  const visibleQuestions = questionsList.filter((q) => !q.deleted);
  return {
    total_questions: visibleQuestions.length,
    active_questions: visibleQuestions.filter((q) => q.active).length,
    required_active: visibleQuestions.filter((q) => q.required && q.active).length,
  };
}

export default function QuestionsPage() {
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(defaultStats);
  const [isLoading, setIsLoading] = useState(true);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [newQuestionType, setNewQuestionType] = useState('mcq');
  const [newAnswers, setNewAnswers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteQ, setDeleteQ] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const activeCount = stats.active_questions;

  useEffect(() => {
    let cancelled = false;

    const fetchQuestions = async () => {
      setIsLoading(true);
      try {
        const response = await getApi('/admin/questions');
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          const { stats: apiStats, questions: apiQuestions } = response.data;
          const normalizedQuestions = (apiQuestions ?? [])
            .map(normalizeQuestion)
            .filter((question) => !question.deleted);
          setQuestions(normalizedQuestions);
          setStats({
            ...defaultStats,
            ...apiStats,
            total_questions: normalizedQuestions.length,
            active_questions:
              normalizedQuestions.filter((q) => q.active).length,
            required_active:
              normalizedQuestions.filter((q) => q.required && q.active).length,
          });
        } else {
          toast.error(response?.message || 'Failed to load questions');
          setQuestions([]);
          setStats(defaultStats);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load questions';
          toast.error(message);
          setQuestions([]);
          setStats(defaultStats);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchQuestions();
    return () => {
      cancelled = true;
    };
  }, []);

  const resetAddForm = () => {
    setNewQuestion('');
    setNewQuestionType('mcq');
    setNewAnswers([]);
    setEditingQuestionId(null);
    setAddOpen(false);
  };

  const openAddModal = () => {
    setNewQuestion('');
    setNewQuestionType('mcq');
    setNewAnswers([]);
    setEditingQuestionId(null);
    setAddOpen(true);
  };

  const openEditModal = (question) => {
    const formType = formTypeFromQuestionType(question.type);
    setNewQuestion(question.question);
    setNewQuestionType(formType);
    setNewAnswers(formType === 'mcq' ? [...(question.answers || [])] : []);
    setEditingQuestionId(question.id);
    setAddOpen(true);
  };

  const handleQuestionTypeChange = (type) => {
    setNewQuestionType(type);
    if (type !== 'mcq') {
      setNewAnswers([]);
    } else if (newAnswers.length === 0) {
      setNewAnswers(['']);
    }
  };

  const addAnswerField = () => {
    if (newAnswers.length >= MAX_ANSWERS) return;
    setNewAnswers((prev) => [...prev, '']);
  };

  const updateAnswer = (index, value) => {
    setNewAnswers((prev) => prev.map((a, i) => (i === index ? value : a)));
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setDeleteQ(null);
  };

  const updateQuestionStatus = async (questionId, active) => {
    return patchApi(`/admin/questions/${questionId}/status`, { active });
  };

  const handleToggleActive = async (question) => {
    if (togglingId === question.id) return;

    const nextActive = !question.active;

    setQuestions((prev) => {
      const next = prev.map((q) => (q.id === question.id ? { ...q, active: nextActive } : q));
      setStats(computeStats(next));
      return next;
    });

    setTogglingId(question.id);
    try {
      const response = await updateQuestionStatus(question.id, nextActive);

      if (response?.statusCode === 200) {
        toast.success(response?.message || `Question ${nextActive ? 'activated' : 'deactivated'}`);
        const updatedQuestion = response?.data?.question ?? response?.data;
        if (updatedQuestion?.id != null) {
          setQuestions((prev) => {
              const next = prev.map((q) => {
                if (q.id !== question.id) return q;
                const normalized = normalizeQuestion({
                  ...q,
                  ...updatedQuestion,
                  is_deleted: q.deleted ? 1 : updatedQuestion?.is_deleted ?? updatedQuestion?.deleted ?? 0,
                });
                return { ...normalized, deleted: q.deleted };
              });
            setStats(computeStats(next));
            return next;
          });
        }
      } else {
        setQuestions((prev) => {
          const next = prev.map((q) => (q.id === question.id ? { ...q, active: !nextActive } : q));
          setStats(computeStats(next));
          return next;
        });
        toast.error(response?.message || 'Failed to update question status');
      }
    } catch (error) {
      setQuestions((prev) => {
        const next = prev.map((q) => (q.id === question.id ? { ...q, active: !nextActive } : q));
        setStats(computeStats(next));
        return next;
      });
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 404 ? 'Question not found' : null) ||
        error?.message ||
        'Failed to update question status';
      toast.error(message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveQuestion = async () => {
    const questionText = newQuestion.trim();
    if (!questionText || isSaving) return;

    const isMcq = newQuestionType === 'mcq';
    const answers = isMcq ? newAnswers.map((a) => a.trim()).filter(Boolean) : [];
    if (isMcq && answers.length === 0) {
      toast.error('Add at least one answer for MCQ questions');
      return;
    }

    if (editingQuestionId) {
      const existingQuestion = questions.find((q) => q.id === editingQuestionId);
      if (!existingQuestion) return;

      const questionType = existingQuestion.type;
      const isMcqEdit = questionType === TYPE_MCQ;
      const editAnswers = isMcqEdit ? newAnswers.map((a) => a.trim()).filter(Boolean) : [];
      if (isMcqEdit && editAnswers.length === 0) {
        toast.error('Add at least one answer for MCQ questions');
        return;
      }

      setIsSaving(true);
      try {
        const payload = buildEditQuestionPayload(questionText, questionType, editAnswers, {
          restore: existingQuestion.deleted,
        });
        const response = await patchApi(`/admin/questions/${editingQuestionId}`, payload);

        if (response?.statusCode === 200) {
          toast.success(response?.message || 'Question updated successfully');
          const updatedQuestion = response?.data?.question ?? response?.data;
          if (updatedQuestion?.id != null) {
            const normalized = normalizeQuestion(updatedQuestion);
            setQuestions((prev) => {
              const next = prev.map((q) => (q.id === editingQuestionId ? normalized : q));
              setStats(computeStats(next));
              return next;
            });
          } else {
            setQuestions((prev) => {
              const next = prev.map((q) =>
                q.id === editingQuestionId
                  ? {
                      ...q,
                      question: questionText,
                      type: questionType,
                      deleted: existingQuestion.deleted ? false : q.deleted,
                      answers:
                        questionType === TYPE_MCQ
                          ? editAnswers
                          : questionType === TYPE_YES_NO
                            ? YES_NO_ANSWERS
                            : [],
                    }
                  : q
              );
              setStats(computeStats(next));
              return next;
            });
          }

          resetAddForm();
        } else {
          toast.error(response?.message || 'Failed to update question');
        }
      } catch (error) {
        const message =
          error?.response?.data?.message || error?.message || 'Failed to update question';
        toast.error(message);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildQuestionPayload(questionText, newQuestionType, answers);
      const response = await postApi('/admin/questions', payload);

      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Question added successfully');
        const createdQuestion = response?.data?.question ?? response?.data;
        if (createdQuestion?.id != null) {
          const normalized = normalizeQuestion(createdQuestion);
          setQuestions((prev) => {
            const next = [...prev, normalized];
            setStats(computeStats(next));
            return next;
          });
        } else {
          const normalized = normalizeQuestion({
            ...payload,
            id: Date.now(),
          });
          setQuestions((prev) => {
            const next = [...prev, normalized];
            setStats(computeStats(next));
            return next;
          });
        }
        resetAddForm();
      } else {
        toast.error(response?.message || 'Failed to add question');
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to add question';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteQ || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await deleteApi(`/admin/questions/${deleteQ.id}`);

      if (response?.statusCode === 200) {
        toast.success(
          response?.message ||
            'Question and related options removed; landlord preferences updated.'
        );
        setQuestions((prev) => {
          const next = prev.filter((q) => q.id !== deleteQ.id);
          setStats(computeStats(next));
          return next;
        });
        setDeleteQ(null);
      } else {
        toast.error(response?.message || 'Failed to delete question');
      }
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 404 ? 'Question not found' : null) ||
        error?.message ||
        'Failed to delete question';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="dashboard-stats admin-questions-stats">
        <AdminStatCard label="Total Questions" value={stats.total_questions} icon="/assets/images/SearchIcon.svg" />
        <AdminStatCard label="Active Questions" value={stats.active_questions} icon="/assets/images/sidebardashboardIcon.svg" />
        <AdminStatCard
          label="Required Active"
          value={stats.required_active}
          icon="/assets/images/UsersTenantDetail.svg"
        />
      </div>

      <div className="admin-toolbar">
        <span style={{ color: '#7c7e93', fontSize: 14 }}>
          {activeCount} active questions
        </span>
        <Button variant="success" onClick={openAddModal}>
          Add Question
        </Button>
      </div>

      <div className="dashboard-section">
        {isLoading ? (
          <p style={{ margin: 0, color: '#7c7e93', fontSize: 14 }}>Loading questions...</p>
        ) : questions.length === 0 ? (
          <p style={{ margin: 0, color: '#7c7e93', fontSize: 14 }}>No questions found.</p>
        ) : null}
        {!isLoading && questions.map((question, index) => (
          <div
            key={question.id}
            className="admin-question-item"
            style={{ opacity: question.active ? 1 : 0.65 }}
          >
            <div className="admin-question-index">{index + 1}</div>
            <div className="admin-question-body">
              <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{question.question}</p>
              {question.answers?.length > 0 && (
                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#7c7e93' }}>
                  Answers: {question.answers.join(', ')}
                </p>
              )}
              <AdminBadge variant="primary">{questionTypeLabel(question.type ?? TYPE_MCQ)}</AdminBadge>{' '}
              {question.required && <AdminBadge variant="warning">Required</AdminBadge>}{' '}
              {question.active ? (
                <AdminBadge variant="success">Active</AdminBadge>
              ) : (
                <AdminBadge variant="muted">Inactive</AdminBadge>
              )}
            </div>
            <div className="admin-question-controls">
              <label
                className="admin-switch"
                title={question.active ? 'Deactivate question' : 'Activate question'}
              >
                <span className="admin-switch-label">{question.active ? 'Active' : 'Inactive'}</span>
                <input
                  type="checkbox"
                  checked={question.active}
                  disabled={togglingId === question.id}
                  onChange={() => handleToggleActive(question)}
                />
                <span className="admin-switch-slider" aria-hidden="true" />
              </label>
              <Button size="small" variant="outline" onClick={() => openEditModal(question)}>Edit</Button>
              <Button size="small" variant="danger" onClick={() => setDeleteQ(question)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <AdminModal
        open={addOpen}
        onClose={resetAddForm}
        title={editingQuestionId ? 'Edit Question' : 'Add New Question'}
        footer={
          <AdminModalFooter
            onCancel={resetAddForm}
            onConfirm={handleSaveQuestion}
            confirmLabel={
              isSaving ? 'Saving...' : editingQuestionId ? 'Save Changes' : 'Add Question'
            }
            confirmDisabled={isSaving || !newQuestion.trim()}
            cancelDisabled={isSaving}
          />
        }
      >
        <Input label="Question" value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} />
        <div className="admin-question-type-group">
          <span className="admin-question-type-label">Question type</span>
          <div className="admin-question-type-options">
            <label className="admin-question-type-option">
              <input
                type="radio"
                name="questionType"
                value="text"
                checked={newQuestionType === 'text'}
                disabled={!!editingQuestionId}
                onChange={() => handleQuestionTypeChange('text')}
              />
              Text
            </label>
            <label className="admin-question-type-option">
              <input
                type="radio"
                name="questionType"
                value="mcq"
                checked={newQuestionType === 'mcq'}
                disabled={!!editingQuestionId}
                onChange={() => handleQuestionTypeChange('mcq')}
              />
              MCQ
            </label>
            <label className="admin-question-type-option">
              <input
                type="radio"
                name="questionType"
                value="yesno"
                checked={newQuestionType === 'yesno'}
                disabled={!!editingQuestionId}
                onChange={() => handleQuestionTypeChange('yesno')}
              />
              Yes / No
            </label>
          </div>
        </div>
        {newQuestionType === 'mcq' && (
          <div className="admin-answers-section">
            <div className="admin-answers-header">
              <span className="admin-answers-label">Add answers</span>
              <button
                type="button"
                className="admin-add-answer-btn"
                onClick={addAnswerField}
                disabled={newAnswers.length >= MAX_ANSWERS}
                aria-label="Add answer"
                title={newAnswers.length >= MAX_ANSWERS ? 'Maximum 4 answers' : 'Add answer'}
              >
                <Plus size={18} strokeWidth={2.5} />
              </button>
            </div>
            {newAnswers.map((answer, index) => (
              <div key={index} className="admin-answer-row">
                <Input
                  label={`Answer ${index + 1}`}
                  value={answer}
                  onChange={(e) => updateAnswer(index, e.target.value)}
                  placeholder={`Enter answer ${index + 1}`}
                />
              </div>
            ))}
          </div>
        )}
        {newQuestionType === 'yesno' && (
          <div className="admin-answers-section">
            <span className="admin-answers-label">Answers</span>
            <div className="admin-yesno-labels">
              {YES_NO_ANSWERS.map((label) => (
                <span key={label} className="admin-yesno-label">
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
      </AdminModal>

      <AdminModal
        open={!!deleteQ}
        onClose={closeDeleteModal}
        title="Delete Question"
        description={
          deleteQ
            ? `Are you sure you want to permanently delete "${deleteQ.question}"? This will remove the question, its answer options, and related landlord preferences. This action cannot be undone.`
            : ''
        }
        footer={
          <AdminModalFooter
            onCancel={closeDeleteModal}
            onConfirm={handleDeleteConfirm}
            confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
            confirmDisabled={isDeleting}
            cancelDisabled={isDeleting}
            danger
          />
        }
      />
    </div>
  );
}
